import * as React from "react";
import { monacoWithSol } from "./solLanguage";
import * as monaco from "monaco-editor";
import "./BaseEditor.scss";
import { isCommented, uncommentText, commentText } from "./utils";
export class BaseEditor extends React.Component {
    constructor(props) {
        super(props);
        this.decorations = [];
        this.actionsToRemove = [
            "editor.action.insertCursorAtEndOfEachLineSelected",
            "editor.action.smartSelect.grow",
            "editor.action.formatDocument",
            "editor.action.formatSelection",
            "editor.action.jumpToBracket",
            "editor.action.selectToBracket",
            "editor.action.showHover",
            "editor.action.smartSelect.shrink",
            "editor.action.addCommentLine",
            "editor.action.removeCommentLine",
            "editor.action.blockComment",
            "editor.action.commentLine",
            "editor.foldAllBlockComments",
        ];
        this.highlightLine = (line) => {
            const editorLine = line + 1;
            this.decorations = this.editor.deltaDecorations(this.decorations, [
                {
                    range: new monacoWithSol.Range(editorLine, 1, editorLine, 1),
                    options: {
                        isWholeLine: true,
                        className: "debugSelectedLine",
                        glyphMarginClassName: "debugGlyphMargin",
                    },
                },
            ]);
            this.editor.revealLine(editorLine);
        };
        this.scrollToBottom = () => {
            const lastLine = this.editor.viewModel.lines.getViewLineCount();
            this.editor.revealLine(lastLine);
        };
        this.editorRef = React.createRef();
    }
    componentDidMount() {
        const baseConfig = this.props.config || {
            language: "solLanguage",
            scrollBeyondLastLine: false,
            theme: this.props.dark ? "vs-dark" : "vs",
        };
        this.editor = monacoWithSol.editor.create(this.editorRef.current, Object.assign(Object.assign({}, baseConfig), { value: this.props.content }));
        if (this.props.onChange !== undefined) {
            const onChange = this.props.onChange;
            this.editor.onDidChangeModelContent(() => {
                onChange(this.editor.getValue());
            });
        }
        if (this.props.line) {
            this.highlightLine(this.props.line);
        }
        if (process.env.NODE_ENV !== "development") {
            this.actionsToRemove.push("editor.action.inspectTokens");
        }
        this.actionsToRemove.forEach((actionId) => {
            const action = this.editor.getAction(actionId);
            action.label = "";
        });
        if (this.props.compileJob) {
            this.editor.addAction({
                id: "compile-job",
                label: "Compile",
                keybindings: [monacoWithSol.KeyMod.Alt | monacoWithSol.KeyCode.KEY_A],
                precondition: null,
                keybindingContext: null,
                contextMenuGroupId: "navigation",
                contextMenuOrder: 1.5,
                run: () => {
                    this.props.compileJob();
                    return null;
                },
            });
        }
        if (this.props.saveContent) {
            this.editor.addAction({
                id: "save-job",
                label: "Save",
                keybindings: [monacoWithSol.KeyMod.Alt | monacoWithSol.KeyCode.KEY_S],
                precondition: null,
                keybindingContext: null,
                contextMenuGroupId: "navigation",
                contextMenuOrder: 1.5,
                run: () => {
                    this.props.saveContent();
                    return null;
                },
            });
        }
        this.editor.addAction({
            id: "toggle-comment",
            label: "Toggle Comment",
            run: (editor) => {
                const position = editor.getSelection();
                const range = Object.assign(Object.assign({}, position), { startColumn: 1 });
                const text = editor.getModel().getValueInRange(range);
                const splitedText = text.split("\n");
                let newText = "";
                if (isCommented(splitedText)) {
                    newText = uncommentText(splitedText);
                }
                else {
                    newText = commentText(splitedText);
                }
                editor.executeEdits("", [
                    {
                        id: { major: 1, minor: 1 },
                        range: range,
                        text: newText,
                        forceMoveMarkers: true,
                    },
                ]);
            },
        });
        this.editor.addAction({
            id: "all_white_spaces",
            label: "Show All White Spaces",
            run: (editor) => {
                editor.updateOptions({ renderWhitespace: "all" });
            },
        });
        this.editor.addAction({
            id: "boundary_white_spaces",
            label: "Show Boundary White Spaces",
            run: (editor) => {
                editor.updateOptions({ renderWhitespace: "boundary" });
            },
        });
        this.editor.addAction({
            id: "hide_white_spaces",
            label: "Hide White Spaces",
            run: (editor) => {
                editor.updateOptions({ renderWhitespace: "none" });
            },
        });
        this.editor.addAction({
            id: "indent_using_spaces",
            label: "Indent Using Spaces",
            run: (editor) => {
                editor.updateOptions({ detectIndentation: false });
                editor.getModel().updateOptions({ insertSpaces: true });
            },
        });
        this.editor.addAction({
            id: "indent_using_tabs",
            label: "Indent Using Tabs",
            run: (editor) => {
                editor.updateOptions({ detectIndentation: false });
                editor.getModel().updateOptions({ insertSpaces: false });
            },
        });
        this.editor.addAction({
            id: "detect_indentation",
            label: "Detect Indentation",
            run: (editor) => {
                editor.updateOptions({ detectIndentation: true });
            },
        });
    }
    componentDidUpdate(prevProps) {
        const { isStream, isStart, height, width, content, isEditable } = this.props;
        this.editor.layout({ width, height });
        if (content !== prevProps.content ||
            (isStream && isStart && this.editor.getValue() !== content)) {
            if (isStream && !isStart && this.editor.getValue() !== "") {
                let model = this.editor.getModel();
                if (model !== null) {
                    let line = model.getLineCount();
                    let range = new monaco.Range(line, 1, line, 1);
                    let op = { range: range, text: content };
                    this.editor.updateOptions({ readOnly: false });
                    this.editor.executeEdits("ADD_STDOUT_CHUNK_" + line, [op]);
                    this.editor.updateOptions({ readOnly: true });
                }
            }
            else {
                this.editor.setValue(content || "");
            }
            if (this.props.scrollToBottom) {
                this.scrollToBottom();
            }
        }
        if (this.props.line) {
            this.highlightLine(this.props.line);
        }
        if (isEditable !== prevProps.isEditable) {
            this.editor.updateOptions({ readOnly: !isEditable });
        }
    }
    componentWillUnmount() {
        if (typeof this.editor !== "undefined") {
            this.editor.dispose();
        }
    }
    render() {
        const { height, width } = this.props;
        return React.createElement("div", { ref: this.editorRef, style: { height, width } });
    }
}
//# sourceMappingURL=BaseEditor.js.map